/* eslint-disable no-console */
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Joyride, STATUS } from "react-joyride";
import { useParams } from "react-router";
import {
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import {
  MenuItem,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  useTheme,
  Box,
  Button,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import SearchIcon from "@mui/icons-material/Search";
import CreateIcon from "@mui/icons-material/Create";
import CreateTopicForm from "@nualang/nualang-ui-components/dist/Forms/CreateTopic";
import TopicTemplateList from "@nualang/nualang-ui-components/dist/Lists/Topics";
import HideOnScroll from "@nualang/nualang-ui-components/dist/Misc/HideOnScroll";
import SortBySelector from "@nualang/nualang-ui-components/dist/Forms/SortBySelector/SortBySelector";
import {
  courses as courseQuerys,
  roleplays as roleplayQuerys,
  bots as botQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import { v4 as uuidv4 } from "uuid";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import AddToCourse from "@nualang/nualang-ui-components/dist/Dialogs/AddToCourse/AddToCourse";
import { UploadTopicImage } from "../../../../utils/imageUpload";
import NualangIcon from "../../../../img/logo/icon.png";
import { highlightSpeechText } from "../../../../utils/index";
import { getCourse } from "@nualang/nualang-api-and-queries/APIs/Courses";
import { getTopics } from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopics";
import useServices from "../../../../hooks/useServices";
import useAudioPlayer from "../../../../hooks/useAudioPlayer";
import {
  createCourseSectionTopic as createCourseSectionTopicAPI,
  duplicateTopicContent,
} from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopics";
import { courses as courseHelpers } from "../../../../utils/helpers";
import { useInView } from "react-intersection-observer";
import CircularProgress from "@mui/material/CircularProgress";
import useDebounce from "@nualang/nualang-ui-components/dist/hooks/useDebounce";
import { getBots } from "@nualang/nualang-api-and-queries/APIs/Bots";
import { getRoleplays } from "@nualang/nualang-api-and-queries/APIs/Roleplays";
import { getCourseSectionTopicPhrases } from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopicPhrases";
import { getCourseSectionTopic } from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopics";
import NualangLogo from "../../../../img/logo/nualang-logo-white.svg";
import i18n from "../../../../i18n";
import useConfirm from "@nualang/nualang-ui-components/dist/hooks/useConfirm";
import { difficulties } from "../../../../utils/index";
import {
  DataType,
  invalidateCache,
} from "@nualang/nualang-api-and-queries/APIs/invalidation";

import logger from "../../../../logger";

// styles
import config from "../../../../config";
import { useSubscription, useApp } from "../../../../hooks";

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(1, 2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2, 3),
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  headerText: {
    flexGrow: 1,
  },
  templateSection: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

function StickyHeader({ classes, children }) {
  return (
    <HideOnScroll threshold={300}>
      <Box
        className={classes.root}
        sx={(theme) => ({
          position: "sticky",
          top: 0,
          zIndex: 1200,
          backgroundColor: theme.palette.background.default,
          borderBottom: `1px solid ${theme.palette.divider}`,
          marginTop: theme.spacing(0.5),
          marginBottom: theme.spacing(0.5),
        })}
      >
        {children}
      </Box>
    </HideOnScroll>
  );
}

export function useCreateCourseSectionTopic(func, username) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => {
      return func(variables.courseId, variables.sectionId, variables.values);
    },
    onError: (error) => {
      // An error happened!
      logger.error(error);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemSections(
          variables.courseId,
          username,
        ),
      });
    },
  });
}

function SearchFilters({
  t,
  classes,
  difficulty,
  handleFilterChange,
  sortByOptions,
  search,
  setSearch,
  difficultyOptions,
  sort,
}) {
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, 500);
  useEffect(() => {
    if (setSearch && searchText !== search) {
      setSearch(searchText);
    }
  }, [debouncedSearch]);
  return (
    <Grid
      container
      direction="row"
      spacing={1}
      className={classes.actions}
      sx={{
        alignItems: "center"
      }}
    >
      <Grid size={{ xs: 12, sm: 3 }}>
        <TextField
          select
          id="difficulty"
          name="difficulty"
          data-cy="difficulty"
          label={t("difficulty")}
          value={difficulty}
          onChange={handleFilterChange}
          margin="dense"
          variant="outlined"
          fullWidth
        >
          {difficultyOptions}
        </TextField>
      </Grid>
      <Grid size={{ xs: 12, sm: 3 }}>
        <SortBySelector
          id={"sortBy"}
          name={"sort"}
          label={t("sort")}
          margin={"dense"}
          variant={"outlined"}
          fullWidth
          t={t}
          value={sort}
          onChange={handleFilterChange}
          sortByOptions={sortByOptions}
        />
      </Grid>
      <Grid
        item
        size={{ xs: 12, md: searchText ? 6 : 3 }}
        sx={(theme) => ({
          transition: theme.transitions.create("all", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        })}
      >
        <TextField
          id="search"
          name="search"
          data-cy="search"
          label={t("search")}
          value={searchText}
          onChange={(ev) => setSearchText(ev.target.value)}
          margin="dense"
          variant="outlined"
          fullWidth
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment
                  position="end"
                  aria-label={t("find_bot")}
                  color="inherit"
                  data-cy="search-bot"
                  size="large"
                >
                  <SearchIcon />
                </InputAdornment>
              ),
            }
          }}
        />
      </Grid>
    </Grid>
  );
}

function CreateTopic(props) {
  const queryClient = useQueryClient();
  const { classes } = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const [confirm] = useConfirm(t);
  const [tts] = useServices();
  const { addToQueue } = useAudioPlayer();
  const [templateType, setTemplateType] = useState("nualang");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("popular");
  const debouncedSearch = useDebounce(search, 500);
  const [myLibrary, setMyLibraryOpen] = useState(false);
  const [teachTopics, setTeachTopics] = useState([]);
  const [, setTimeoutArray] = useState([]);
  const [filters, setFilters] = useState({
    difficulty: "all",
    learnLang: "",
    forLang: "",
    orderBy: "",
    limit: 12,
    visibility: "public",
    returnvalues: "",
    lastEvaluatedKey: "",
    lastEvaluatedCreatedAt: "",
    lastEvaluatedExerciseStartTotal: "",
    sort: "popular",
    search: "",
    verbose: false,
    page: 1,
  });
  const {
    navigate,
    authData: { attributes, username },
    verificationStatus,
    isCommunityAccessRestricted,
  } = props;

  const params = useParams();
  const { classroomId, courseId, sectionId } = params;

  const isUserInternal = config.internalDomains.some((domain) =>
    (attributes.email || "").endsWith(domain),
  );

  const courseQuery = courseQuerys.useCourse(
    async (cId) => {
      const course = await getCourse(cId);
      const formattedCourse = await courseHelpers.formatCourse(
        course.Item,
        props.authData,
      );
      return formattedCourse;
    },
    {
      courseId,
      username: props.authData.username,
    },
    { enabled: !!courseId },
  );

  const course = useMemo(
    () =>
      courseQuery.isSuccess && courseQuery.data && courseQuery.data.courseId
        ? courseQuery.data
        : {},
    [courseQuery.data, courseQuery.isSuccess],
  );

  useEffect(() => {
    if (filters.learnLang !== course.learnLang) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        learnLang: course.learnLang,
      }));
    }
    if (filters.search !== debouncedSearch) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        search: debouncedSearch,
      }));
    }
  }, [filters.learnLang, filters.search, course.learnLang, debouncedSearch]);

  const {
    handleOpenModerateDialog,
    openSnackbar,
    startLoading,
    stopLoading,
  } = useApp();

  const {subscription} = useSubscription()

  const { ref, inView } = useInView();

  const {
    data = {},
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: [...courseQuerys.topicKeys.list(filters), "create"],
    queryFn: async ({ pageParam }) => {
      try {
        const topics = await getTopics({
          ...filters,
          page: pageParam.page || 1,
          lastEvaluatedCreatedAt: pageParam.createdAt
            ? pageParam.createdAt
            : "",
          lastEvaluatedKey:
            pageParam.topicId && pageParam.courseSectionId
              ? `${pageParam.courseSectionId}/${pageParam.topicId}`
              : "",
          lastEvaluatedExerciseStartTotal:
            pageParam.exerciseStartTotal || pageParam.exerciseStartTotal === 0
              ? pageParam.exerciseStartTotal
              : "",
        });
        const formattedTopics = await courseHelpers.formatTopics(
          topics.Items,
          false,
          props.authData,
        );
        return {
          ...topics,
          Items: formattedTopics,
        };
      } catch (error) {
        openSnackbar("search_error", "error");
        throw error;
      }
    },
    initialPageParam: {},
    getNextPageParam: (lastPage) => {
      if (
        lastPage.Items.length > 0 &&
        (lastPage.LastEvaluatedKey?.page ||
          (lastPage.LastEvaluatedKey?.topicId &&
            lastPage.LastEvaluatedKey?.courseSectionId))
      ) {
        return lastPage.LastEvaluatedKey;
      }
      return undefined;
    },
    enabled: !isCommunityAccessRestricted,
  });

  const getTeachTopics = async () => {
    const teachTopics = await getTopics({
      createdBy: username,
      limit: config.limits.topics,
      learnLang: course.learnLang,
    });
    const formattedTeachTopics = await courseHelpers.formatTopics(
      teachTopics.Items,
      false,
      props.authData,
    );
    const topics = formattedTeachTopics.filter(
      (topic) => topic.courseSectionId !== `${courseId}|${sectionId}`,
    );
    setTeachTopics(topics);
  };

  useEffect(() => {
    getTeachTopics();
  }, [myLibrary]);

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  const allItems = useMemo(
    () => (data.pages || []).flatMap((page) => page.Items),
    [data],
  );

  const handleFilterChange = (event) => {
    const { target } = event;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const { name } = target;

    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
      page: 1,
      lastEvaluatedKey: "",
      lastEvaluatedCreatedAt: "",
      lastEvaluatedExerciseStartTotal: "",
    }));

    if (name === "sort") {
      setSort(value);
    } else if (name === "search") {
      setSearch(value);
    }

    if (window.scrollY > 100) {
      window.scrollTo(0, 0);
    }
  };

  const sortByOptions = {
    newest: "newest",
    oldest: "oldest",
    popular: "popular",
  };

  // Mutations
  const createCourseSectionTopicMutation = useCreateCourseSectionTopic(
    createCourseSectionTopicAPI,
    username,
  );

  const joyrideSteps = [
    {
      content: <h2>{t("lets_find_a_template")}</h2>,
      placement: "center",
      target: "body",
    },
    {
      content: (
        <div>
          <h2>{t("select_a_nualang_template")}</h2>
          <br />
          <p>{t("select_a_nualang_template_desc")}</p>
        </div>
      ),
      placement: "center",
      target: "body",
    },
    // {
    //   content: <h2>{t('search_by_difficulty')}</h2>,
    //   spotlightPadding: 20,
    //   target: '#difficulty',
    // },
    // {
    //   content: <h2>{t('search_using_keywords')}</h2>,
    //   spotlightPadding: 20,
    //   target: '#search',
    // },
  ];

  if (!isCommunityAccessRestricted) {
    joyrideSteps.splice(2, 0, {
      content: (
        <div>
          <h2>{t("or_select_a_community_template")}</h2>
          <br />
          <p>{t("or_select_a_community_template_desc")}</p>
        </div>
      ),
      spotlightPadding: 20,
      target: "#community-templates",
    });
  }

  const [joyrideState, setJoyrideState] = useState({
    run: false,
    steps: joyrideSteps,
  });

  // Restricted users are always kept on the Nualang/library templates, regardless of state.
  const effectiveTemplateType = isCommunityAccessRestricted
    ? "nualang"
    : templateType;

  const handleClickCommunityTemplates = () => {
    if (isCommunityAccessRestricted) return;
    setTemplateType("community");
  };

  const handleClickNualangTemplates = () => {
    setTemplateType("nualang");
  };

  useEffect(() => {
    if (!localStorage.getItem("find-a-topic-template-tour-completed")) {
      setJoyrideState((prevState) => ({
        ...prevState,
        run: true,
      }));
    }
  }, []);

  const [initialValues, setInitialValues] = useState({
    topicName: "",
    description: "",
    difficulty: "all",
    picture: course.picture ? course.picture : "",
    banner: course.banner ? course.banner : "",
    pictureS3Key: course.pictureS3Key ? course.pictureS3Key : "",
    bannerS3Key: course.bannerS3Key ? course.bannerS3Key : "",
    forLang: course.forLang,
    learnLang: course.learnLang,
    visibility: "private",
    tags: [],
    notes: "",
    selectedTemplate: "",
    topicGoal: "",
  });

  useEffect(() => {
    setInitialValues((prevState) => ({
      ...prevState,
      learnLang: course.learnLang,
      forLang: course.forLang,
    }));
  }, [course.learnLang, course.forLang]); // Only re-run the effect if the values change

  const [filteredNualangTemplates, setFilteredNualangTemplates] =
    useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);

  const handleSearch = useCallback(() => {
    let initTemplates = [];

    if (course && course.learnLang) {
      initTemplates = config.languages[course.learnLang].topicBlueprints.map(
        (t) => ({
          ...t,
          userImage: NualangIcon,
          createdByName: "Nualang",
        }),
      );
    }
    setFilteredNualangTemplates(initTemplates);
  }, [course]);

  useEffect(() => {
    handleSearch();
  }, [handleSearch]);
  // Only re-run the effect if the difficulty or search changes

  const difficultyOptions = difficulties.map((d, idx) => (
    <MenuItem key={`diffccretopic_${idx}`} value={d}>
      {t(d)}
    </MenuItem>
  ));

  const handleSelectTopicTemplate = async (topic) => {
    const selectedTemplateTopic = window.structuredClone(topic);
    const selectedTemplateTopicId = selectedTemplateTopic.topicId;
    setSelectedTopic({ ...selectedTemplateTopic });

    const topicAttribtesToRemove = [
      "courseSectionId",
      "topicId",
      "createdBy",
      "createdByName",
      "createdAt",
      "updatedAt",
      "completions",
      "bots",
      "phrases",
      "roleplays",
      "exerciseCompletionTotal",
      "exerciseStartTotal",
    ];

    topicAttribtesToRemove.forEach((a) => delete selectedTemplateTopic[a]);

    let selectedDifficulty = course ? course.difficulty : "A1";
    if (filters.difficulty && filters.difficulty.toLowerCase() !== "all") {
      selectedDifficulty = filters.difficulty;
    } else if (
      selectedTemplateTopic.difficulty &&
      selectedTemplateTopic.difficulty.toLowerCase() !== "all"
    ) {
      selectedDifficulty = selectedTemplateTopic.difficulty;
    }
    setInitialValues((prevState) => ({
      ...prevState,
      ...selectedTemplateTopic,
      selectedTemplate: selectedTemplateTopicId,
      visibility: "private",
      createdByName: attributes ? attributes.preferred_username : null,
      userImage: attributes ? attributes.picture : null,
      difficulty: selectedDifficulty,
      forLang: course ? course.forLang : null,
    }));
    window.scrollTo(0, 0);
  };

  const createCourseSectionTopic = (
    courseId,
    sectionId,
    values,
    topicTemplate,
    setSubmitting,
  ) => {
    if (!courseId || !sectionId || !values || !values.topicName) {
      openSnackbar("missing_details", "error");
      return;
    }
    startLoading();
    return createCourseSectionTopicMutation.mutateAsync(
      {
        courseId,
        sectionId,
        values: {
          ...values,
          phrases: [],
          idx: Date.now(),
        },
      },
      {
        onSuccess: async (data) => {
          let newPhrases = [],
            newRoleplays = [],
            newBots = [];
          if (topicTemplate) {
            const responses = await duplicateTopicContent(
              topicTemplate,
              data.Item,
            );
            newPhrases = (responses[0] || []).map((r) => r.Item);
            newRoleplays = (responses[1] || []).map((r) => r.Item);
            newBots = (responses[2] || []).map((r) => r.Item);
          }
          await invalidateCache(DataType.TOPIC_DUPLICATE, {
            courseId,
            sectionId,
            topicId: data.Item.topicId,
          });

          setSubmitting(false);
          stopLoading();
          openSnackbar("created", "success");

          queryClient.setQueryData(
            courseQuerys.topicKeys.item(
              courseId,
              sectionId,
              data.Item.topicId,
              username,
            ),
            {
              ...data.Item,
              phrases: newPhrases,
              roleplays: newRoleplays,
              bots: newBots,
            },
          );


          queryClient.invalidateQueries({
            queryKey: courseQuerys.topicKeys.item(
              courseId,
              sectionId,
              data.Item.topicId,
              username
            ),
          });

          queryClient.setQueryData(
            roleplayQuerys.roleplayKeys.list({
              courseSectionTopicId: `${courseId}|${sectionId}|${data.Item.topicId}`,
            }),
            newRoleplays,
          );

          queryClient.setQueryData(
            botQuerys.botKeys.list({
              courseSectionTopicId: `${courseId}|${sectionId}|${data.Item.topicId}`,
            }),
            newBots,
          );

          if (data && data.Item && data.Item.topicId) {
            const { topicId } = data.Item;
            if (classroomId && courseId && sectionId && topicId) {
              navigate(
                `/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}`,
              );
            } else if (courseId && sectionId && topicId) {
              navigate(`/courses/${courseId}/${sectionId}/${topicId}`);
            } else {
              navigate("/courses");
            }
          }
        },
        onError: () => {
          setSubmitting(false);
          stopLoading();
          openSnackbar("problem_creating", "error");
        },
      },
    );
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Remove all null values recursively
      const removeNulls = (obj) => {
        if (Array.isArray(obj)) {
          return obj
            .map(removeNulls)
            .filter((value) => value !== null && value !== undefined);
        }

        if (obj && typeof obj === "object") {
          return Object.keys(obj).reduce((acc, key) => {
            const value = removeNulls(obj[key]);

            if (value !== null && value !== undefined) {
              acc[key] = value;
            }

            return acc;
          }, {});
        }

        return obj;
      };

      values = removeNulls(values);
      const { visibility, difficulty, tags } = values;
      if (
        (visibility !== "public" && visibility !== "private") ||
        !difficulties.includes(difficulty) ||
        !Array.isArray(tags)
      ) {
        //input validation
        throw new Error();
      }
      if (values.picture) {
        values.pictureWarnDetails = {};
        const base64String = values.picture.split(",");
        const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
          await UploadTopicImage(base64String[1] || base64String[0], uuidv4());
        if (moderated || warn) {
          if (verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            if (confirmation !== true) {
              return;
            }
            values.pictureWarnDetails = {
              user: username,
              labels: moderatedLabels.join(", "),
            };
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (picture) {
          values.picture = picture;
          values.pictureS3Key = pictureS3Key;
        } else {
          delete values.picture;
        }
      }
      delete values.topicId;
      delete values.exerciseCompletionTotal;
      delete values.exerciseStartTotal;

      const topicTemplate =
        selectedTopic &&
        selectedTopic.topicId &&
        !selectedTopic.topicId.startsWith("nualang")
          ? selectedTopic
          : null;
      await createCourseSectionTopic(
        courseId,
        sectionId,
        values,
        topicTemplate,
        setSubmitting,
      );
    } catch (error) {
      openSnackbar("Error creating topic", "error");
      logger.error(error);
    }
  };

  const handleSpeak = async (params) => {
    try {
      const {
        text,
        learnLanguage,
        voiceName,
        mute,
        callback,
        textContainer,
        languageCode,
        pitch,
      } = params;
      const result = await tts(
        text,
        learnLanguage,
        voiceName,
        languageCode,
        null,
        pitch,
      );
      if (!mute && result && result.audioUrl) {
        const highlightCallback = () =>
          highlightSpeechText(
            textContainer,
            result.speechMarks,
            setTimeoutArray,
          );
        addToQueue(result.audioUrl, callback, highlightCallback);
      } else if (result.errorMessage) {
        throw new Error(result.errorMessage);
      }
      return result;
    } catch (error) {
      logger.error(error);
      return null;
    }
  };

  const handleJoyrideCallback = (data) => {
    const { status, action } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status) || action === "close") {
      setJoyrideState((prevState) => ({
        ...prevState,
        run: false,
      }));
      localStorage.setItem("find-a-topic-template-tour-completed", "true");
    }
  };

  const { run, steps } = joyrideState;
  return (
    <div>
      {run && (
        <Joyride
          onEvent={handleJoyrideCallback}
          continuous={true}
          run={run}
          scrollToFirstStep={true}
          steps={steps}
          locale={{
            back: t("back"),
            close: t("close"),
            last: t("last"),
            next: t("next"),
            nextWithProgress: t("next_with_progress"),
            skip: t("skip"),
          }}
          options={{
            showProgress: true,
            zIndex: 10000,
            arrowColor: theme.palette.background.paper,
            backgroundColor: theme.palette.background.paper,
            primaryColor: theme.palette.primary.main,
            textColor: theme.palette.text.primary,
            buttons: ['back', 'close', 'primary', 'skip']
          }} />
      )}
      <AddToCourse
        t={t}
        open={myLibrary}
        handleClose={() => {
          setMyLibraryOpen(false);
        }}
        exercises={teachTopics ? teachTopics : []}
        title={t("my_library")}
        desc={t("add_own_exercise_message", {
          exerciseName: t("topics").toLowerCase(),
        })}
        exerciseName={t("topics").toLowerCase()}
        handleSelectTemplate={(topic) => {
          handleSelectTopicTemplate(topic);
          setMyLibraryOpen(false);
        }}
      />
      {!initialValues.selectedTemplate && (
        <React.Fragment>
          {effectiveTemplateType !== "nualang" ? (
            <>
              <StickyHeader classes={classes}>
                <div className={classes.header}>
                  <div className={classes.headerText}>
                    <Grid
                      container
                      direction="row"
                      sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                        display: "flex"
                      }}>
                      <Typography variant="h5" component="h2">
                        {t("select_a_template")}
                      </Typography>
                      <Grid>
                        <Button
                          sx={{ marginRight: 1 }}
                          onClick={() => {
                            setMyLibraryOpen(true);
                          }}
                          color="primary"
                          variant={"contained"}
                          startIcon={<SwitchAccountIcon />}
                        >
                          {t("my_library")}
                        </Button>
                        <Button
                          onClick={handleClickNualangTemplates}
                          color="primary"
                          variant={"contained"}
                          style={{
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "7px",
                          }}
                          startIcon={<CreateIcon />}
                        >
                          {i18n.language === "en" ||
                          i18n.language === "en-US" ||
                          i18n.language === "de" ||
                          i18n.language === "pt" ? (
                            <>
                              <img
                                src={NualangLogo}
                                style={{ width: "75px" }}
                                alt="Logo"
                              />
                              {t("nualang_templates")}
                            </>
                          ) : (
                            <>
                              {t("nualang_templates")}
                              <img
                                src={NualangLogo}
                                style={{ width: "75px" }}
                                alt="Logo"
                              />
                            </>
                          )}
                        </Button>
                      </Grid>
                    </Grid>
                  </div>
                </div>
                <SearchFilters
                  t={t}
                  classes={classes}
                  difficulty={filters.difficulty}
                  difficultyOptions={difficultyOptions}
                  handleFilterChange={handleFilterChange}
                  sortByOptions={sortByOptions}
                  learnLang={course.learnLang}
                  search={search}
                  setSearch={setSearch}
                  sort={sort}
                />
              </StickyHeader>
              <div className={classes.root}>
                <div className={classes.templateSection}>
                  <TopicTemplateList
                    t={t}
                    topics={isLoading ? null : allItems}
                    handleSelectTopic={handleSelectTopicTemplate}
                    subscription={subscription}
                    disablePreview={false}
                    getBots={getBots}
                    getRoleplays={getRoleplays}
                    getPhrases={getCourseSectionTopicPhrases}
                    getCourseSectionTopic={getCourseSectionTopic}
                  />
                  {allItems && allItems.length === 0 && !isLoading && (
                    <Typography>{t("no_results_found")}</Typography>
                  )}
                  <Box
                    ref={ref}
                    sx={{
                      textAlign: "center",
                      mt: 1
                    }}>
                    {isFetchingNextPage || isLoading ? (
                      <CircularProgress />
                    ) : hasNextPage ? (
                      <CircularProgress />
                    ) : (
                      ""
                    )}
                  </Box>
                </div>
              </div>
            </>
          ) : (
            <>
              <StickyHeader classes={classes}>
                <div className={classes.header}>
                  <div className={classes.headerText}>
                    <Grid
                      container
                      direction="row"
                      sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                        display: "flex"
                      }}>
                      <Typography variant="h5" component="h2">
                        {t("select_a_template")}
                      </Typography>
                      <Grid>
                        <Button
                          sx={{ marginRight: 1 }}
                          onClick={() => {
                            setMyLibraryOpen(true);
                          }}
                          color="primary"
                          variant={"contained"}
                          startIcon={<SwitchAccountIcon />}
                        >
                          {t("my_library")}
                        </Button>
                        {!isCommunityAccessRestricted && (
                          <Button
                            onClick={handleClickCommunityTemplates}
                            color="primary"
                            variant={"contained"}
                            startIcon={<SearchIcon />}
                            id="community-templates"
                          >
                            {t("community_templates")}
                          </Button>
                        )}
                      </Grid>
                    </Grid>
                  </div>
                </div>
              </StickyHeader>
              <div className={classes.root}>
                <div className={classes.templateSection}>
                  <TopicTemplateList
                    t={t}
                    topics={filteredNualangTemplates}
                    handleSelectTopic={handleSelectTopicTemplate}
                    subscription={subscription}
                    disablePlays
                  />
                  {filteredNualangTemplates &&
                    filteredNualangTemplates.length === 0 && (
                      <Typography>{t("no_results_found")}</Typography>
                    )}
                </div>
              </div>
            </>
          )}
        </React.Fragment>
      )}
      {initialValues.selectedTemplate && (
        <div className={classes.root}>
          <CreateTopicForm
            t={t}
            onSubmit={handleSubmit}
            initialValues={initialValues}
            fileSizeLimit={config.fileSizeLimit}
            subscription={subscription}
            handleSpeak={handleSpeak}
            voices={
              initialValues && initialValues.learnLang
                ? config.languages[
                    initialValues.learnLang.toLowerCase() || "english"
                  ].Voices
                : []
            }
            learnLang={initialValues?.learnLang}
            isUserInternal={isUserInternal}
          />
        </div>
      )}
    </div>
  );
}

CreateTopic.propTypes = {
  navigate: PropTypes.func,
  authData: PropTypes.object,
  isCommunityAccessRestricted: PropTypes.bool,
};

export default CreateTopic;
